import * as React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { Grid, Box, Card, Typography } from "@mui/material";
import moment from "moment";
import MDBox from "components/MDBox";
import colors from "assets/theme/base/colors";
import LeaderBoard from "components/LeaderBoard";
import { DashboardSummary } from "types/Dashboard";
type Props = {
  dashboardSummary: DashboardSummary;
};

const ChartComponent: React.FC<Props> = (props) => {
  const { dashboardSummary } = props;

  // Generate labels for the past 12 weeks, starting each week on Monday
  const generateWeeklyLabels = () => {
    return Array.from(
      { length: 12 },
      (_, i) =>
        `Week ${i + 1} (${moment()
          .startOf("isoWeek") // Start on Monday of the current week
          .subtract(11 - i, "weeks") // Go back in time for each week
          .format("MM/DD")})`
    );
  };

  // Generate labels for the past 12 months
  const generateMonthlyLabels = () => {
    return Array.from({ length: 12 }, (_, i) =>
      moment().subtract(i, "month").format("MMM YYYY")
    ).reverse();
  };

  // Generate labels for the past 7 days (daily stamps)
  const generateDailyLabels = () => {
    return Array.from({ length: 7 }, (_, i) =>
      moment()
        .subtract(i, "days") // Go back one day at a time
        .format("ddd (MM/DD)")
    ).reverse(); // Reverse the order to show the most recent dates last
  };
  return (
    <Box sx={{ flexGrow: 1, padding: "20px" }}>
      <Grid container spacing={2}>
        {/* Bar Chart for Hourly Stamps */}
        <Grid item xs={12} md={6}>
          <Card sx={{ width: "100%", height: 400 }}>
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              padding={1.5}
              sx={{ textAlign: "center" }}
            >
              Number of Stamps Collected Each Hour
            </Typography>

            <BarChart
              xAxis={[
                {
                  label: "Each Hour of Day",
                  data: Array.from({ length: 24 }, (_, i) => `${i}:00`),
                  scaleType: "band"
                }
              ]}
              series={[{ data: dashboardSummary.hourly_stamps }]}
              colors={[colors.success.main]}
              title="Number of Stamps Collected Each Hour"
            />
          </Card>
        </Grid>

        {/* Bar Chart for Daily Stamps */}
        <Grid item xs={12} md={6}>
          <Card sx={{ width: "100%", height: 400 }}>
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              padding={1.5}
              sx={{ textAlign: "center" }}
            >
              Stamps Collected Per Day Over the Past Week
            </Typography>
            <BarChart
              xAxis={[
                {
                  label: "Day of Week",
                  data: generateDailyLabels(),
                  scaleType: "band"
                }
              ]}
              series={[{ data: dashboardSummary.daily_stamps }]}
              colors={[colors.primary.main]}
            />
          </Card>
        </Grid>

        {/* Line Chart for Weekly Stamps */}
        <Grid item xs={12} md={6}>
          <Card sx={{ width: "100%", height: 400 }}>
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              padding={1.5}
              sx={{ textAlign: "center" }}
            >
              Weekly Stamps Collection for the Past 12 Weeks
            </Typography>
            <LineChart
              xAxis={[
                {
                  label: "Week",
                  data: generateWeeklyLabels(),
                  scaleType: "band"
                }
              ]}
              series={[{ data: dashboardSummary.weekly_stamps }]}
              colors={[colors.info.main]}
            />
          </Card>
        </Grid>

        {/* Line Chart for Monthly Stamps */}
        <Grid item xs={12} md={6}>
          <Card sx={{ width: "100%", height: 400 }}>
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              padding={1.5}
              sx={{ textAlign: "center" }}
            >
              Stamps Collected Per Month Over the Past Year
            </Typography>
            <LineChart
              xAxis={[
                {
                  label: "Month",
                  data: generateMonthlyLabels(),
                  scaleType: "band"
                }
              ]}
              series={[{ data: dashboardSummary.monthly_stamps }]}
            />
          </Card>
        </Grid>

        {/* Line Chart for Monthly New Customers */}
        <Grid item xs={12} md={6}>
          <Card sx={{ width: "100%", height: 400 }}>
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              padding={1.5}
              sx={{ textAlign: "center" }}
            >
              Returning Customers Each Month
            </Typography>
            <LineChart
              xAxis={[
                {
                  label: "Month",
                  data: generateMonthlyLabels(),
                  scaleType: "band"
                }
              ]}
              series={[
                {
                  data: dashboardSummary.monthly_returning_customers,
                  area: true
                }
              ]}
              colors={[colors.error.focus]}
            />
          </Card>
        </Grid>

        {/* Bar Chart for Monthly Voucher Orders */}
        <Grid item xs={12} md={6}>
          <Card sx={{ width: "100%", height: 400 }}>
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              padding={1.5}
              sx={{ textAlign: "center" }}
            >
              Comparison of New vs Returning Customers for Past 7 Days
            </Typography>
            <PieChart
              series={[
                {
                  highlightScope: { faded: "global", highlighted: "item" },
                  faded: {
                    innerRadius: 30,
                    additionalRadius: -30,
                    color: "gray"
                  },
                  innerRadius: 30,
                  outerRadius: 100,
                  paddingAngle: 5,
                  cornerRadius: 5,
                  startAngle: -90,
                  data: [
                    {
                      id: 0,
                      value: dashboardSummary.customers_by_week
                        ? dashboardSummary.customers_by_week.new_customers
                        : 0,
                      label: "New Customers"
                    },
                    {
                      id: 1,
                      value: dashboardSummary.customers_by_week
                        ? dashboardSummary.customers_by_week.returning_customers
                        : 0,
                      label: "Returning Customers"
                    }
                  ]
                }
              ]}
              colors={[colors.info.focus, colors.primary.main]}
            />
          </Card>
        </Grid>
        {/* Pie Chart for Customers by Day */}
        <Grid item xs={12} md={6}>
          <Card sx={{ width: "100%", height: 400 }}>
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              padding={1.5}
              sx={{ textAlign: "center" }}
            >
              Voucher Orders Placed Each Month
            </Typography>
            <BarChart
              xAxis={[
                {
                  label: "Month",
                  data: generateMonthlyLabels(),
                  scaleType: "band"
                }
              ]}
              series={[
                {
                  data: dashboardSummary.monthly_voucher_orders
                }
              ]}
              colors={[colors.info.focus]}
            />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ width: "100%", height: 400 }}>
            <MDBox mb={1.5} back>
              <LeaderBoard data={dashboardSummary.top_customers} />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
export default ChartComponent;
